import React, { useEffect, useState } from "react";
import { Badge, Spinner, Modal, Table, OverlayTrigger, Tooltip, Button } from "react-bootstrap";
import {
  FiArrowRight,
  FiPackage,
  FiTruck,
  FiUser,
  FiInfo,
  FiX,
  FiTrendingUp,
  FiDollarSign,
  FiShoppingBag
} from "react-icons/fi";
import logo from "../images/TP-_logo-orizz-claim-color.png";
import "bootstrap/dist/css/bootstrap.min.css";

const WidgetCrossDocking = () => {
  const [flussi, setFlussi] = useState([]);
  const [totaleGiornaliero, setTotaleGiornaliero] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showFullscreenModal, setShowFullscreenModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((data) => {
        const tuttiGliOrdini = data?.CROSS_DOCKING || [];

        // Calcoliamo la data di oggi in formato YYYY-MM-DD usando l'ora LOCALE e non UTC
        const adesso = new Date();
        const offset = adesso.getTimezoneOffset() * 60000;
        const oggiLocale = new Date(adesso - offset).toISOString().split("T")[0];

        // FILTRO: teniamo solo i record la cui data_doc corrisponde a oggi
        const listaRaw = tuttiGliOrdini.filter((item) => {
          const dataStr = item.data_doc ? String(item.data_doc).split(" ")[0] : null;
          return dataStr === oggiLocale;
        });

        // 1. CALCOLO TOTALE GIORNALIERO PURO (Basato su Importo Cliente, solo oggi)
        const totaleG = listaRaw.reduce((acc, item) => {
          const valore = parseFloat(item["Importo cli"]) || 0;
          return acc + valore;
        }, 0);

        // 2. Raggruppiamo i dati di oggi per la visualizzazione nelle Card / Modale
        const gruppi = listaRaw.reduce((acc, item) => {
          const key = `${item.Doc_Cliente}_${item.Doc_Fornitore}`;
          const importoCliente = parseFloat(item["Importo cli"]) || 0;
          const importoFornitore = parseFloat(item["Importo for"]) || 0;

          if (!acc[key]) {
            acc[key] = {
              Doc_Cliente: item.Doc_Cliente,
              Doc_Fornitore: item.Doc_Fornitore,
              Cliente: item.Cliente,
              Fornitore: item.Fornitore,
              Nazione_Cliente: item["NAZIONE CLIENTE"],
              Nazione_Fornitore: item["NAZIONE FORNITORE"],
              Regione_Fornitore: item["REGIONE FORNITORE"],
              Provincia_Fornitore: item["PROVINCIA FORNITORE"],
              data_doc: item.data_doc,
              articoli: [],
              totaleCliente: 0,
              totaleFornitore: 0
            };
          }

          acc[key].articoli.push({
            articolo: item.articolo,
            precodice: item.precodice,
            quantita: parseFloat(item.quantita) || 1,
            importoCli: importoCliente,
            importoFor: importoFornitore
          });

          // Somme incrementali cumulative per flusso
          acc[key].totaleCliente += importoCliente;
          acc[key].totaleFornitore += importoFornitore;
          return acc;
        }, {});

        // Convertiamo l'oggetto in array e calcoliamo i margini preventivamente per l'ordinamento/card
        const arrayFlussi = Object.values(gruppi).map(flusso => {
          const profitto = flusso.totaleCliente - flusso.totaleFornitore;
          const percentualeGuadagno = flusso.totaleCliente > 0 ? (profitto / flusso.totaleCliente) * 100 : 0;
          return {
            ...flusso,
            profitto,
            percentualeGuadagno
          };
        });

        // 3. ORDINAMENTO PER NUMERO DOCUMENTO FORNITORE (Decrescente)
        arrayFlussi.sort((a, b) => {
          return String(b.Doc_Fornitore).localeCompare(String(a.Doc_Fornitore), undefined, {
            numeric: true,
            sensitivity: "base",
          });
        });

        setFlussi(arrayFlussi);
        setTotaleGiornaliero(totaleG);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore Cross Docking:", err);
        setLoading(false);
      });
  }, []);

  const handleOpenDettaglio = (flusso) => {
    setSelectedOrder(flusso);
    setShowModal(true);
  };

  const renderTooltipSpiegazione = (props) => (
    <Tooltip id="info-crossdocking-tooltip" {...props} style={{ ...props.style, fontSize: '0.85rem' }}>
      Questo widget monitora i flussi di <strong>Cross Docking</strong> attivi oggi. Mostra la merce in arrivo da un fornitore (Provenienza) che viene immediatamente smistata verso il cliente finale (Destinatario) senza stoccaggio intermedio. <strong>Clicca su una riga</strong> per analizzare il dettaglio degli articoli e il totale economico.
    </Tooltip>
  );

  const getMargineTextColorClass = (percentuale) => {
    if (percentuale >= 20) return "text-success";
    if (percentuale >= 10) return "text-warning";
    return "text-danger";
  };

  const getMargineBadgeColorClass = (percentuale) => {
    if (percentuale >= 20) return "bg-success text-white";
    if (percentuale >= 10) return "bg-warning text-dark";
    return "bg-danger text-white";
  };

  const renderCardsContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5 w-100">
          <Spinner animation="border" variant="danger" />
        </div>
      );
    }

    if (flussi.length === 0) {
      return <div className="text-center py-5 text-muted small w-100">Nessun flusso di Cross Docking attivo oggi</div>;
    }

    return flussi.map((flusso, idx) => (
      <div key={idx} className="col">
        <div className="cd-card" onClick={() => handleOpenDettaglio(flusso)}>
          <div className="flow-line">

            {/* Nodo Fornitore */}
            <div className="node text-start">
              <div className="node-label">
                <FiTruck className="me-1" /> Provenienza ({flusso.Nazione_Fornitore})
              </div>
              <div className="node-doc text-dark">ORD FOR: {flusso.Doc_Fornitore}</div>
              <div className="node-name text-dark" title={flusso.Fornitore}>{flusso.Fornitore}</div>
              <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>
                {flusso.Regione_Fornitore} ({flusso.Provincia_Fornitore})
              </small>
            </div>

            {/* Centro di smistamento / Badge & Margine */}
            <div className="arrow-box">
              <Badge
                bg="transparent"
                className="border border-danger text-danger mb-1"
                style={{ fontSize: "0.55rem" }}
              >
                {flusso.articoli.length} {flusso.articoli.length === 1 ? 'ARTICOLO' : 'ARTICOLI'}
              </Badge>
              
              {/* Box Margine Esterno */}
              <div 
                className={`d-flex align-items-center gap-1 fw-bold ${getMargineTextColorClass(flusso.percentualeGuadagno)}`} 
                style={{ fontSize: "0.68rem", letterSpacing: "0.2px" }}
              >
                <FiTrendingUp size={10} />
                <span>{flusso.percentualeGuadagno.toFixed(1)}%</span>
              </div>

              <FiArrowRight className="arrow-icon" />
            </div>

            {/* Nodo Cliente */}
            <div className="node text-end">
              <div className="node-label">
                <FiUser className="me-1" /> Destinatario ({flusso.Nazione_Cliente})
              </div>
              <div className="node-doc text-dark">ORD CLI: {flusso.Doc_Cliente}</div>
              <div className="node-name text-dark" title={flusso.Cliente}>{flusso.Cliente}</div>
            </div>

          </div>
        </div>
      </div>
    ));
  };

  return (
    <div
      className="shadow-lg card"
      style={{
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "350px",
      }}
    >
      {/* Header Widget */}
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white"
        style={{ zIndex: 110 }}
      >
        <div className="d-flex align-items-center" onClick={() => setShowFullscreenModal(true)} style={{ cursor: "pointer" }}>
          <span
            style={{
              fontWeight: "800",
              fontSize: "0.75rem",
              color: "#1a1a1a",
              letterSpacing: "1px",
              marginRight: "8px",
            }}
          >
            CROSS DOCKING - OGGI
          </span>

          <OverlayTrigger
            placement="bottom"
            delay={{ show: 200, hide: 200 }}
            overlay={renderTooltipSpiegazione}
          >
            <span onClick={(e) => e.stopPropagation()} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
              <FiInfo size={14} color="#adb5bd" />
            </span>
          </OverlayTrigger>
        </div>

        <div className="d-flex gap-2 align-items-center">
          <Badge
            pill
            bg="dark"
            className="px-2 py-1.5 d-flex align-items-center gap-1"
            style={{ fontSize: "0.65rem", fontWeight: "700" }}
          >
            {flussi.length} FLUSSI
          </Badge>

          <Badge
            pill
            bg="success"
            className="px-2 py-1.5 d-flex align-items-center gap-1 shadow-sm text-uppercase"
            style={{ fontSize: "0.70rem", fontWeight: "700", letterSpacing: "0.5px" }}
          >
            Fatt. Clienti: €{totaleGiornaliero.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Badge>
        </div>
      </div>

      <style>{`
        .scroll-area-cd { overflow-y: auto; flex-grow: 1; padding: 15px; }
        .scroll-area-cd::-webkit-scrollbar { width: 4px; }
        .scroll-area-cd::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 10px; }

        .cd-card {
            border: 1px solid #e6e6e6; border-radius: 12px;
            padding: 18px; cursor: pointer;
            transition: all 0.2s ease-in-out; position: relative;
            background: #fff;
        }
        .cd-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
            border-color: #b81f25;
        }
        .cd-card::before {
            content: ''; position: absolute; left: 0; top: 15%; height: 70%; width: 3px;
            background: #b81f25; border-radius: 0 4px 4px 0;
        }

        .flow-line { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
        .node { flex: 1; min-width: 0; }
        .node-label { font-size: 0.6rem; color: #6c757d; font-weight: 800; text-transform: uppercase; margin-bottom: 4px; }
        .node-doc { font-size: 0.85rem; font-weight: 700; color: #1a1a1a; font-family: 'Monaco', monospace; }
        .node-name { font-size: 0.75rem; color: #495057; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        .arrow-box { display: flex; flex-direction: column; align-items: center; color: #b81f25; min-width: 75px; }
        .arrow-icon { animation: slideRight 2s infinite; font-size: 1.1rem; margin-top: 2px; }

        @keyframes slideRight {
            0% { transform: translateX(-4px); opacity: 0.4; }
            50% { transform: translateX(4px); opacity: 1; }
            100% { transform: translateX(-4px); opacity: 0.4; }
        }

        .custom-modal-content {
          max-width: 700px !important;
          width: 100% !important;
          margin: 0 auto !important;
          border-radius: 20px !important;
          box-shadow: 0 15px 40px rgba(0,0,0,0.15) !important;
        }

        .kpi-container {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        .kpi-card {
          padding: 14px;
          border-radius: 12px;
          background: #fdfdfd;
          border: 1px solid #eaeaea;
          transition: all 0.2s ease;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 10px rgba(0,0,0,0.03);
        }
        .kpi-title { font-size: 0.65rem; font-weight: 700; color: #8c8c8c; text-transform: uppercase; margin-bottom: 4px; letter-spacing: 0.5px;}
        .kpi-value { font-size: 1rem; font-weight: 800; color: #1a1a1a; }

        .custom-progress-bar {
          height: 6px;
          background-color: #e9ecef;
          border-radius: 4px;
          overflow: hidden;
        }

        @media (max-width: 576px) {
          .custom-modal-content { max-width: calc(100% - 1rem) !important; }
          .kpi-container { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Lista standard per il widget */}
      <div className="scroll-area-cd">
        <div className="row row-cols-1 g-3">
          {renderCardsContent()}
        </div>
      </div>

      {/* MODALE TUTTO SCHERMO */}
      <Modal show={showFullscreenModal} onHide={() => setShowFullscreenModal(false)} fullscreen={true} animation={true}>
        <Modal.Body className="p-0 d-flex flex-column" style={{ background: "#f8f9fa" }}>
          <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center shadow-sm" style={{ zIndex: 110 }}>
            <div className="d-flex align-items-center">
              <span style={{ fontWeight: "800", fontSize: "0.9rem", color: "#1a1a1a", letterSpacing: "1px", marginRight: "8px" }}>
                VISTA COMPLETA CROSS DOCKING - OGGI
              </span>
              <OverlayTrigger placement="bottom" overlay={renderTooltipSpiegazione}>
                <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}><FiInfo size={16} color="#adb5bd" /></span>
              </OverlayTrigger>
            </div>

            <div className="d-flex gap-2 align-items-center">
              <Badge bg="dark" className="px-3 py-2" style={{ fontSize: "0.75rem", fontWeight: "700" }}>
                {flussi.length} FLUSSI ATTIVI
              </Badge>
              <Badge bg="success" className="px-3 py-2" style={{ fontSize: "0.75rem", fontWeight: "700" }}>
                VALORE TOTALE: € {totaleGiornaliero.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Badge>
              <Button variant="outline-dark" className="d-flex align-items-center gap-1 border-0" onClick={() => setShowFullscreenModal(false)} style={{ borderRadius: "10px", height: "38px" }}>
                <FiX size={16} /> Chiudi
              </Button>
            </div>
          </div>

          <div className="flex-grow-1 p-4" style={{ overflowY: "auto" }}>
            <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4">
              {renderCardsContent()}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* MODALE DETTAGLIO: FINANCIAL ANALYTICS */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        className="modal-light-custom"
        contentClassName="custom-modal-content"
      >
        <Modal.Header closeButton className="px-4 pt-4 border-0 bg-white">
          <Modal.Title
            className="fw-extrabold d-flex align-items-center gap-2"
            style={{ fontSize: "1.05rem", color: "#b81f25", letterSpacing: "0.3px" }}
          >
            <FiTrendingUp size={20} /> LOGISTICS FINANCIAL INTELLIGENCE
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-white px-4 pb-4 pt-0">

          {/* Box Rotte di Spedizione */}
          <div className="info-strip-light p-3 mb-4 d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
            <div style={{ flex: 1 }}>
              <small className="text-muted d-block fw-bold" style={{ fontSize: "0.6rem", letterSpacing: "0.5px" }}>SUPPLIER SOURCE</small>
              <span className="fw-bold text-dark d-block text-truncate" style={{ fontSize: "0.85rem" }}>{selectedOrder?.Fornitore}</span>
              <div className="text-muted fw-semibold mt-1" style={{ fontSize: "0.7rem", fontFamily: 'monospace' }}>DOC FOR: {selectedOrder?.Doc_Fornitore}</div>
              <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                {selectedOrder?.Provincia_Fornitore} - {selectedOrder?.Regione_Fornitore} ({selectedOrder?.Nazione_Fornitore})
              </div>
            </div>

            <div className="px-1 text-danger d-none d-sm-block align-self-center">
              <FiArrowRight size={22} className="opacity-75" />
            </div>

            <div style={{ flex: 1 }} className="text-start text-sm-end">
              <small className="text-muted d-block fw-bold" style={{ fontSize: "0.6rem", letterSpacing: "0.5px" }}>CUSTOMER DESTINATION</small>
              <span className="fw-bold text-dark d-block text-truncate" style={{ fontSize: "0.85rem" }}>{selectedOrder?.Cliente}</span>
              <div className="text-muted fw-semibold mt-1" style={{ fontSize: "0.7rem", fontFamily: 'monospace' }}>DOC CLI: {selectedOrder?.Doc_Cliente}</div>
              <div className="text-muted" style={{ fontSize: "0.7rem" }}>{selectedOrder?.Nazione_Cliente}</div>
            </div>
          </div>

           {/* Tabella Dettaglio Articoli */}
          <span className="d-block fw-extrabold text-muted mb-2" style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}>BREAKDOWN COMPONENTI DI COSTO</span>
          <Table responsive hover className="mb-5 table-light-custom align-middle">
            <thead>
              <tr style={{ borderBottom: "2px solid #f1f1f1" }}>
                <th className="pb-2" style={{ fontSize: "0.65rem" }}>Articolo / Codice</th>
                <th className="text-center pb-2" style={{ fontSize: "0.65rem" }}>Q.tà</th>
                <th className="text-end pb-2" style={{ fontSize: "0.65rem" }}>Vendita (Cli)</th>
                <th className="text-end pb-2 pe-2" style={{ fontSize: "0.65rem" }}>Acquisto (For)</th>
              </tr>
            </thead>
            <tbody>
              {selectedOrder?.articoli?.map((art, i) => {
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #f8f9fa" }}>
                    <td className="py-2.5">
                      <div className="fw-bold text-dark text-truncate" style={{ fontSize: "0.78rem", maxWidth: "240px" }}>
                        {art.articolo}
                      </div>
                      <div className="text-muted" style={{ fontSize: "0.68rem" }}>
                        Precodice: <span className="text-dark fw-medium">{art.precodice || "N/A"}</span>
                      </div>
                    </td>
                    <td className="text-center py-2.5 text-dark fw-semibold" style={{ fontSize: "0.75rem" }}>
                      {art.quantita.toFixed(0)}
                    </td>
                    <td className="text-end py-2.5 fw-bold text-primary" style={{ fontSize: "0.78rem" }}>
                      € {art.importoCli.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="text-end py-2.5 pe-2 fw-bold text-secondary" style={{ fontSize: "0.78rem" }}>
                      € {art.importoFor.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {/* Griglia dei KPI Finanziari */}
          <div className="kpi-container mb-4">
            <div className="kpi-card" style={{ borderLeft: "4px solid #0d6efd" }}>
              <div className="kpi-title"><FiShoppingBag className="me-1"/> Ricavo Cliente</div>
              <div className="kpi-value text-primary">
                € {(selectedOrder?.totaleCliente || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            <div className="kpi-card" style={{ borderLeft: "4px solid #6c757d" }}>
              <div className="kpi-title"><FiShoppingBag className="me-1 text-danger"/> Costo Fornitore</div>
              <div className="kpi-value text-secondary">
                € {(selectedOrder?.totaleFornitore || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            <div className="kpi-card" style={{ borderLeft: "4px solid #198754" }}>
              <div className="kpi-title d-flex justify-content-between align-items-center">
                <span><FiTrendingUp className="me-1"/> Margine Netto</span>
                <Badge className={`rounded-pill px-1.5 py-0.5 style={{ fontSize: "0.55rem" }} ${getMargineBadgeColorClass(selectedOrder?.percentualeGuadagno)}`}>
                  +{selectedOrder?.percentualeGuadagno ? selectedOrder.percentualeGuadagno.toFixed(1) : "0.0"}%
                </Badge>
              </div>
              <div className="kpi-value text-success">
                € {(selectedOrder?.profitto || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

        </Modal.Body>

        <Modal.Footer className="bg-light border-top-0 justify-content-center py-2.5" style={{ borderBottomBottomRadius: "20px" }}>
          <img
            src={logo}
            alt="Logo"
            style={{ width: "85px", filter: "grayscale(1)", opacity: 0.4 }}
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WidgetCrossDocking;